function getZoomAdjustedViewportWidth() {
  if (typeof window === 'undefined') {
    return 0;
  }

  const viewport = window.visualViewport;
  if (viewport && typeof viewport.width === 'number' && viewport.width > 0) {
    return Math.round(viewport.width);
  }

  const doc = document.documentElement;
  return window.innerWidth || (doc && doc.clientWidth) || 0;
}

class CustomProductGallery {
  constructor() {
    this.images = Array.from(document.querySelectorAll('.custom-image-item'));
    this.viewportWidth = getZoomAdjustedViewportWidth();
    this.baseViewportHeight = this.measureViewportHeight();
    this.viewportHeight = this.baseViewportHeight;
    this.initialViewportScale = window.visualViewport && typeof window.visualViewport.scale === 'number'
      ? window.visualViewport.scale
      : 1;
    this.desktopBreakpoint = 990;
    this.isTouch = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    this.ticking = false;
    this.isInitialized = false; // Track initialization state
    // Width state (actual & target) to smooth changes
    this.currentWidths = [];
    this.targetWidths = [];
    this.animationFrame = null;
    // Track which images have been "passed" (scrolled past at full size)
    this.passedFullSize = [];
    // Track when last image is at full size to trigger content rise
    this.lastImageAtFullSize = false;
    // Buy buttons positioning
    this.buyButtons = document.querySelector('.custom-buy-buttons');
    this.productTabs = document.querySelector('.custom-product-tabs');
    this.buyButtonsPositioned = false;
    this.originalParent = null;
    this.resizeRaf = null;

    this.setInitialWidths();
    this.bindEvents();

    // Mark as initialized immediately after setting widths
    this.isInitialized = true;

    this.onScroll(); // initialize target widths
    this.startAnimationLoop(); // begin width smoothing loop
  }

  isDesktopGalleryMode() {
    return this.viewportWidth >= this.desktopBreakpoint;
  }

  getMediaElement(wrapper) {
    return wrapper.querySelector('img, video, iframe');
  }

  bindEvents() {
    window.addEventListener('scroll', () => this.requestTick(), { passive: true });
    window.addEventListener('resize', () => this.handleResize());

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', () => this.handleResize(), { passive: true });
    }
  }

  requestTick() {
    if (!this.ticking) {
      requestAnimationFrame(() => {
        this.onScroll();
        this.ticking = false;
      });
      this.ticking = true;
    }
  }

  measureViewportHeight() {
    const { visualViewport } = window;
    if (visualViewport && visualViewport.height) {
      return Math.round(visualViewport.height);
    }

    const docElement = document.documentElement;
    const layoutViewport = docElement && docElement.clientHeight ? docElement.clientHeight : 0;
    const innerHeight = window.innerHeight || 0;

    if (layoutViewport && innerHeight) {
      return Math.min(layoutViewport, innerHeight);
    }

    return innerHeight || layoutViewport || 0;
  }

  handleResize() {
    if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);

    this.resizeRaf = requestAnimationFrame(() => {
      this.resizeRaf = null;

      const measuredHeight = this.measureViewportHeight();
      const viewport = window.visualViewport;
      const currentScale = viewport && typeof viewport.scale === 'number' ? viewport.scale : 1;
      const zoomDelta = Math.abs(currentScale - this.initialViewportScale);
      const isZoomResize = viewport && zoomDelta > 0.01;

      if (isZoomResize) {
        return;
      }
      const newViewportWidth = getZoomAdjustedViewportWidth();
      const widthChanged = Math.abs(newViewportWidth - this.viewportWidth) > 1;
      const hasMeasuredHeight = typeof measuredHeight === 'number' && measuredHeight > 0;
      const tolerance = 8;
      const previousBase = this.baseViewportHeight || 0;
      const heightDelta = hasMeasuredHeight && previousBase ? measuredHeight - previousBase : 0;
      const heightDecreased = hasMeasuredHeight && (!previousBase || heightDelta < -tolerance);
      const heightIncreased = hasMeasuredHeight && heightDelta > tolerance;

      // Always update on width change, even if minimal
      if (widthChanged || heightDecreased || heightIncreased) {
        this.viewportWidth = newViewportWidth || this.viewportWidth;

        let baseUpdated = false;

        if (widthChanged && hasMeasuredHeight) {
          // Orientation change or real resize — always accept the new height
          this.baseViewportHeight = measuredHeight;
          baseUpdated = true;
        } else if (heightDecreased && !widthChanged) {
          // Height decreased without width change — likely pull-to-refresh rubber-band,
          // address bar appearing, or keyboard opening. Do NOT shrink the base height
          // so the gallery stays at full viewport height.
        } else if (heightIncreased && !widthChanged) {
          // Height increased without width change — allow recovery to full height
          // (e.g. after pull-to-refresh bounce-back, or address bar hiding).
          this.baseViewportHeight = measuredHeight;
          baseUpdated = true;
        }

        if (!previousBase && hasMeasuredHeight && !baseUpdated) {
          // No baseline yet — accept whatever we have
          this.baseViewportHeight = measuredHeight;
          baseUpdated = true;
        }

        if (baseUpdated) {
          this.viewportHeight = this.baseViewportHeight;
        }

        // Always recalculate widths and scroll positions on any resize
        this.setInitialWidths();
        this.onScroll();
      }

      // Always reset buy buttons positioning on resize
      if (this.buyButtons) {
        if (this.originalParent && this.buyButtons.parentElement !== this.originalParent) {
          this.originalParent.appendChild(this.buyButtons);
        }
        this.buyButtons.classList.remove('positioned-below-tabs');
        this.buyButtons.style.top = '';
        this.buyButtonsPositioned = false;
      }
    });
  }

  getInitialSize(index) {
    // Base scales: first image 1.0, all others 0.5
    if (index === 0) return 1;
    const clamped = 0.5;
    return clamped;
  }

  setInitialWidths() {
    this.viewportHeight = this.baseViewportHeight || this.viewportHeight;

    if (!this.isDesktopGalleryMode()) {
      this.images.forEach((wrapper, idx) => {
        const mediaEl = this.getMediaElement(wrapper);
        if (!mediaEl) return;
        mediaEl.style.transition = 'none';
        mediaEl.style.transform = '';
        wrapper.style.removeProperty('height');
        this.currentWidths[idx] = null;
        this.targetWidths[idx] = null;
        this.passedFullSize[idx] = false;
      });
      return;
    }

    // First pass: set basic properties and initial values, ensuring all wrappers have a proper height
    this.images.forEach((wrapper, idx) => {
      const mediaEl = this.getMediaElement(wrapper);
      if (!mediaEl) return;
      const s = this.getInitialSize(idx);
      // Reset inline transform properties
      mediaEl.style.removeProperty('width');
      mediaEl.style.removeProperty('max-width');
      mediaEl.style.transition = 'none';
      mediaEl.style.display = 'block';
      mediaEl.style.margin = '0';
      mediaEl.style.transform = '';
      this.currentWidths[idx] = s;
      this.targetWidths[idx] = s;
      this.passedFullSize[idx] = false;

      // On touch devices, keep all wrappers at fixed viewport height so
      // document height never changes during scroll (prevents iOS
      // momentum-scroll jump).  On desktop, wrapper tracks the scale.
      wrapper.style.height = this.isTouch
        ? this.viewportHeight + 'px'
        : (s * this.viewportHeight) + 'px';

      // On touch devices, anchor images to the top of their wrapper so
      // scaled-down images don't leave a gap above.
      if (this.isTouch) {
        mediaEl.style.transformOrigin = 'center top';
        wrapper.style.alignItems = 'flex-start';
      }
    });

    // Second pass: check current scroll position and update accordingly
    this.checkInitialScrollPosition();
  }

  checkInitialScrollPosition() {
    if (!this.isDesktopGalleryMode()) {
      return;
    }

    const centerY = this.viewportHeight / 2;
    const lastImageIndex = this.images.length - 1;

    this.images.forEach((wrapper, idx) => {
      // Read each rect individually after previous images may have changed height,
      // so that subsequent positions account for cumulative height adjustments.
      // Batch-reading all rects upfront caused stale positions on scroll-restored reloads.
      const rect = wrapper.getBoundingClientRect();
      const initial = this.getInitialSize(idx);

      if (!rect) return;

      const mid = rect.top + rect.height / 2;

      // If image center is above viewport center, it should be considered "passed"
      if (mid < centerY) {
        this.passedFullSize[idx] = true;
        this.currentWidths[idx] = 1;
        this.targetWidths[idx] = 1;
      } else {
        // Calculate scale based on distance from center with larger band
        const dist = Math.abs(mid - centerY);
        const band = this.viewportHeight * 0.75; // Increased from 0.4 to 0.75 for earlier scaling
        let progress = 1 - dist / band;
        if (progress < 0) progress = 0;
        if (progress > 1) progress = 1;

        const scale = initial + (1 - initial) * progress;
        this.currentWidths[idx] = scale;
        this.targetWidths[idx] = scale;
      }      // Apply the calculated scale and height
      const mediaEl = this.getMediaElement(wrapper);
      if (mediaEl) {
        mediaEl.style.transform = `scale(${this.currentWidths[idx]})`;
      }
      wrapper.style.height = this.isTouch
        ? this.viewportHeight + 'px'
        : (this.currentWidths[idx] * this.viewportHeight) + 'px';
    });

    // Check initial state of last image for product content animation
    if (lastImageIndex >= 0) {
      const lastImageScale = this.targetWidths[lastImageIndex];
      this.lastImageAtFullSize = lastImageScale >= 0.95;
    }
  }
  onScroll() {
    // Skip processing if not fully initialized to prevent race conditions
    if (!this.isInitialized) {
      return;
    }

    if (!this.isDesktopGalleryMode()) {
      this.handleBuyButtonsPositioning();
      return;
    }

    // Read phase: collect rects first to limit layout thrash
    const centerY = this.viewportHeight / 2;
    const rects = this.images.map(el => el.getBoundingClientRect());
    const lastImageIndex = this.images.length - 1;

    // Process each image for scaling - NO HYSTERESIS
    this.images.forEach((wrapper, idx) => {
      const rect = rects[idx];
      const initial = this.getInitialSize(idx);

      if (!rect) {
        this.targetWidths[idx] = this.passedFullSize[idx] ? 1 : initial;
        return;
      }

      const isAboveCenterBand = rect.bottom <= centerY;
      if (isAboveCenterBand) {
        // Consider any image whose bottom cleared the center line as "passed"
        if (!this.passedFullSize[idx]) {
          this.passedFullSize[idx] = true;
        }
        this.targetWidths[idx] = 1;
        return;
      }

      if (rect.bottom <= 0 || rect.top >= this.viewportHeight) {
        // Image not visible yet or already reset below center
        this.targetWidths[idx] = this.passedFullSize[idx] ? 1 : initial;
        return;
      }

      const mid = rect.top + rect.height / 2;
      const dist = Math.abs(mid - centerY);
      const band = this.viewportHeight * 0.75; // Increased from 0.4 to 0.75 for earlier scaling

      let progress = 1 - dist / band;
      if (progress < 0) progress = 0;
      if (progress > 1) progress = 1;

      // Basic linear scaling - no easing
      const scale = initial + (1 - initial) * progress;      // Check if this image reached near full size and is now above center
      if (scale > 0.95 && mid < centerY) {
        this.passedFullSize[idx] = true;
        this.targetWidths[idx] = 1; // Lock at full size
      } else if (this.passedFullSize[idx] && mid > centerY) {
        // Image center is back below viewport center, allow it to scale down again
        this.passedFullSize[idx] = false;
        this.targetWidths[idx] = scale;
      } else if (this.passedFullSize[idx]) {
        // Keep at full size if it was passed
        this.targetWidths[idx] = 1;
      } else {
        // Normal scaling behavior
        this.targetWidths[idx] = scale;
      }
    });

    // Check if last image is at full size and trigger product content animation
    if (lastImageIndex >= 0) {
      const lastImageScale = this.targetWidths[lastImageIndex];
      const isLastImageAtFullSize = lastImageScale >= 0.95;

      if (isLastImageAtFullSize !== this.lastImageAtFullSize) {
        this.lastImageAtFullSize = isLastImageAtFullSize;
      }
    }

    // Handle buy buttons positioning on mobile
    this.handleBuyButtonsPositioning();
  }

  handleBuyButtonsPositioning() {
    // Only run on mobile screens
    if (getZoomAdjustedViewportWidth() > 649) return;

    if (!this.buyButtons || !this.productTabs) return;

    // Find the home-featured-collection section first, fallback to product container
    const homeFeaturedCollection = document.querySelector('.home-featured-collection, .featured-collection');
    const productContainer = document.querySelector('.custom-product-container');
    const stopElement = homeFeaturedCollection || productContainer;

    if (!stopElement) return;

    const scrollY = window.scrollY;
    const currentScrollPosition = scrollY + window.innerHeight;

    const elementRect = stopElement.getBoundingClientRect();
    const buffer = homeFeaturedCollection ? 100 : 0;
    const threshold = elementRect.top + scrollY - buffer;

    const shouldActivate = currentScrollPosition > threshold && !this.buyButtonsPositioned;
    const shouldDeactivate = currentScrollPosition <= threshold && this.buyButtonsPositioned;

    if (shouldActivate) {
      // Store original parent if not already stored
      if (!this.originalParent) {
        this.originalParent = this.buyButtons.parentElement;
      }

      // Move buy buttons to appear right after the tabs
      this.productTabs.insertAdjacentElement('afterend', this.buyButtons);

      // Add CSS class to trigger relative positioning
      this.buyButtons.classList.add('positioned-below-tabs');
      this.buyButtonsPositioned = true;
    } else if (shouldDeactivate) {
      // Move buy buttons back to original position
      if (this.originalParent) {
        this.originalParent.appendChild(this.buyButtons);
      }

      // Remove CSS class to return to fixed positioning
      this.buyButtons.classList.remove('positioned-below-tabs');
      this.buyButtonsPositioned = false;
    }
  }

  applyWidth(wrapper, w) {
    if (!this.isDesktopGalleryMode()) {
      wrapper.style.removeProperty('height');
      const mediaReset = this.getMediaElement(wrapper);
      if (mediaReset) {
        mediaReset.style.transform = '';
      }
      return;
    }

    const mediaEl = this.getMediaElement(wrapper);
    if (!mediaEl) return;
    // Direct transform application - no animation
    mediaEl.style.transform = `scale(${w})`;

    // On touch devices, wrapper stays at fixed viewport height.
    // On desktop, wrapper tracks the scale.
    wrapper.style.height = this.isTouch
      ? this.viewportHeight + 'px'
      : (w * this.viewportHeight) + 'px';
  }

  startAnimationLoop() {
    const animate = () => {
      let changed = false;
      this.images.forEach((wrapper, idx) => {
        const target = this.targetWidths[idx];
        if (target == null) return;
        if (this.currentWidths[idx] === target) return;

        // Direct application - no smoothing
        this.currentWidths[idx] = target;
        this.applyWidth(wrapper, target);
        changed = true;
      });
      this.animationFrame = requestAnimationFrame(animate);
    };
    this.animationFrame = requestAnimationFrame(animate);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  if (document.querySelector('.custom-image-item')) {
    window.customProductGallery = new CustomProductGallery();
  }
});

// Initialize buy buttons positioning even if there are no images
document.addEventListener('DOMContentLoaded', () => {
  const buyButtons = document.querySelector('.custom-buy-buttons');
  const productTabs = document.querySelector('.custom-product-tabs');

  if (buyButtons && productTabs && !window.customProductGallery) {
    let buyButtonsPositioned = false;

    let originalParent = null;

    // Create a minimal instance just for buy buttons positioning
    const scrollHandler = () => {
      if (getZoomAdjustedViewportWidth() > 649) return;

      // Find the home-featured-collection section first, fallback to product container
      const homeFeaturedCollection = document.querySelector('.home-featured-collection, .featured-collection');
      const productContainer = document.querySelector('.custom-product-container');
      const stopElement = homeFeaturedCollection || productContainer;

      if (!stopElement) return;

      const scrollY = window.scrollY;
      const currentScrollPosition = scrollY + window.innerHeight;

      const elementRect = stopElement.getBoundingClientRect();
      const buffer = homeFeaturedCollection ? 100 : 0;
      const threshold = elementRect.top + scrollY - buffer;

      const shouldActivate = currentScrollPosition > threshold && !buyButtonsPositioned;
      const shouldDeactivate = currentScrollPosition <= threshold && buyButtonsPositioned;

      if (shouldActivate) {
        // Store original parent if not already stored
        if (!originalParent) {
          originalParent = buyButtons.parentElement;
        }

        // Move buy buttons to appear right after the tabs
        productTabs.insertAdjacentElement('afterend', buyButtons);

        // Add CSS class to trigger relative positioning
        buyButtons.classList.add('positioned-below-tabs');
        buyButtonsPositioned = true;
      } else if (shouldDeactivate) {
        // Move buy buttons back to original position
        if (originalParent) {
          originalParent.appendChild(buyButtons);
        }

        // Remove CSS class to return to fixed positioning
        buyButtons.classList.remove('positioned-below-tabs');
        buyButtonsPositioned = false;
      }
    };

    window.addEventListener('scroll', scrollHandler, { passive: true });
    window.addEventListener('resize', () => {
      // Move buy buttons back to original position if needed
      if (originalParent && buyButtons.parentElement !== originalParent) {
        originalParent.appendChild(buyButtons);
      }
      buyButtons.classList.remove('positioned-below-tabs');
      buyButtonsPositioned = false;
    });
  }
});
